import React, { useState, useRef, useEffect } from 'react';
import Select from 'react-select';
import close from '../../Assets/popup-close.svg';

const MultiSelectDropdown = ({options, onChange, valueSet, placeHolder, handleRemoveData}) => {
  const [selectedOptions, setSelectedOptions] = useState(valueSet || []);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const containerRef = useRef(null);
  const dragStartTime = useRef(0);
  // NEW: Add a ref to control the menu state
  const selectRef = useRef(null);
  // NEW: Track if we're scrolling to prevent menu opening
  const isScrolling = useRef(false);

  const handleChange = (selected) => {
    setSelectedOptions(selected);
    onChange(selected);
  };

  useEffect(() => {
    // CHANGE: Compare arrays properly before updating
    const areArraysEqual = (arr1, arr2) => {
      if (!arr1 || !arr2) return false;
      if (arr1.length !== arr2.length) return false;
      
      const valueMap = new Map();
      arr1.forEach(item => valueMap.set(item.value, item));
      
      return arr2.every(item => valueMap.has(item.value));
    };
    
    // Only update if the arrays are actually different
    if (!areArraysEqual(selectedOptions, valueSet)) {
      setSelectedOptions(valueSet || []);
    }
  }, [valueSet]);

  // NEW: Add effect to maintain scroll position
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    // Store the scroll position when scrolling ends
    const handleScroll = () => {
      setScrollLeft(container.scrollLeft);
    };
    
    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleRemoveValue = (valueToRemove) => {    
    // Create a new array of selected options, excluding the removed value
    const newSelected = selectedOptions.filter(
      option => option.value !== valueToRemove.value
    );
    
    // Update the local state
    setSelectedOptions(newSelected);
    
    // Call the parent component's remove handler
    handleRemoveData(valueToRemove);
    
    // Trigger onChange to update parent component's state
    onChange(newSelected);
  };

  const handleMouseDown = (e) => {
    if (!containerRef.current) return;
    // NEW: Set flag to indicate we're potentially scrolling
    isScrolling.current = true;
    dragStartTime.current = Date.now();
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
    
    // NEW: Prevent dropdown from opening when scrolling
    e.stopPropagation();
  };

  const handleMouseUp = (event) => {
    setIsDragging(false);
    const dragDuration = Date.now() - dragStartTime.current;
    
    // NEW: Store the final scroll position
    if (containerRef.current) {
      setScrollLeft(containerRef.current.scrollLeft);
    }
    
    // NEW: Reset scrolling flag after a short delay
    setTimeout(() => {
      isScrolling.current = false;
    }, 100);
    
    if (dragDuration < 150) {
      // It was a click, not a drag
      if (event.target.classList.contains('remove-button')) {
        const value = event.target.getAttribute('data-value');
        const optionToRemove = selectedOptions.find(opt => opt.value === value);
        if (optionToRemove) {
          handleRemoveValue(optionToRemove);
        }
      }
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();
    // NEW: Make sure we're in scrolling mode
    isScrolling.current = true;
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchStart = (e) => {
    if (!containerRef.current) return;
    // NEW: Set flag to indicate we're potentially scrolling
    isScrolling.current = true;
    dragStartTime.current = Date.now();
    setIsDragging(true);
    setStartX(e.touches[0].pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
    
    // NEW: Prevent dropdown from opening when scrolling
    e.stopPropagation();
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !containerRef.current) return;
    // NEW: Make sure we're in scrolling mode
    isScrolling.current = true;
    const x = e.touches[0].pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  // NEW: Add touch end handler to ensure scroll position is saved
  const handleTouchEnd = (e) => {
    setIsDragging(false);
    
    // NEW: Store the final scroll position
    if (containerRef.current) {
      setScrollLeft(containerRef.current.scrollLeft);
    }
    
    setTimeout(() => {
      isScrolling.current = false;
    }, 100);
  };

  // NEW: Custom onMenuOpen handler to prevent dropdown opening during scrolling
  const handleMenuOpen = () => {
    if (isScrolling.current) {
      // Close the menu if it's opened during scrolling
      if (selectRef.current && selectRef.current.select) {
        selectRef.current.select.closeMenu();
      }
      return;
    }
  };

  const customStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '35px',
      height: '35px',
      width: '450px',
      minWidth: '450px',
      display: 'flex',
      position: 'relative',
      overflow: 'hidden',
      borderColor: state.isFocused ? '#02acf6' : '#757ea5',
      border: "1px solid #757EA5",
    }),
    valueContainer: (base) => ({
      ...base,
      height: '35px',
      padding: '0 6px',
      position: 'static',
      overflow: 'hidden',
      flex: '1 1 auto',
      display: 'flex',
      flexWrap: 'nowrap',
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        display: 'none'
      },
      cursor: isDragging ? 'grabbing' : 'grab',
      whiteSpace: 'nowrap',
      alignItems: 'center',
    }),
    multiValue: (base) => ({
      ...base,
      background: '#f3fbff',
      borderRadius: '5px',
      padding: '0px 5px',
      display: 'flex',
      flex: '0 0 auto',
      margin: '0 2px',
      height: '24px',
      alignItems: 'center',
      userSelect: 'none',
    }),
    multiValueRemove: (base) => ({
      ...base,
      display: 'inline-flex',
      cursor: 'pointer',
      padding: '0 10px',
      height: '15px',
      width: '15px'
    }),
    indicatorsContainer: (base) => ({
      ...base,
      position: 'absolute',
      right: 0,
      top: 0,
      height: '100%',
      background: 'white',
      display: 'flex',
      alignItems: 'center',
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: '4px',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '4px',
    }),
    container: (base) => ({
      ...base,
      width: '450px',
    }),
    input: (base) => ({
      ...base,
      margin: '0',
      padding: '0',
    }),
    placeholder: (base) => ({
      ...base,
      margin: '0',
    }),
  };

  return (
    <div className="relative">
      <Select
        ref={selectRef}
        isMulti
        value={selectedOptions}
        onChange={handleChange}
        options={options}
        placeholder={placeHolder}
        styles={customStyles}
        // NEW: Handle menu opening
        onMenuOpen={handleMenuOpen}
        // NEW: Capture clicks on the control to prevent menu opening during scroll
        onControlMouseDown={(e) => {
          if (isScrolling.current) {
            e.stopPropagation();
          }
        }}
        components={{
          ValueContainer: ({ children, ...props }) => {
            // NEW: Set up ref function that both assigns the ref and maintains scroll position
            const setRef = (el) => {
              containerRef.current = el;
              if (el && scrollLeft > 0) {
                // This ensures scroll position is maintained when component rerenders
                el.scrollLeft = scrollLeft;
              }
            };
            
            return (
              <div
                ref={setRef}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                // NEW: Add onClick handler to prevent dropdown when scrolling
                onClick={(e) => {
                  if (isScrolling.current) {
                    e.stopPropagation();
                    e.preventDefault();
                  }
                }}
                style={{
                  height: '35px',
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  overflow: 'hidden',
                  msOverflowStyle: 'none', // Hides scrollbar for IE/Edge
                  scrollbarWidth: 'thin',  // For Firefox
                  scrollbarColor: '#02acf6 #f1f1f1', // For Firefox (thumb and track colors)
                  WebkitOverflowScrolling: 'touch', // Smooth scrolling
                  padding: '0 40px 0 6px',
                }}
              >
                {children}
                <style>
                  {`
                    /* Custom scrollbar styles */
                    ::-webkit-scrollbar {
                      width: 8px;
                    }
                    ::-webkit-scrollbar-track {
                      background: #f1f1f1;
                    }
                    ::-webkit-scrollbar-thumb {
                      background: #CFD4E9;
                      border-radius: 4px;
                    }
                    ::-webkit-scrollbar-thumb:hover {
                      background: #CFD4E9;
                    }
                    ::-webkit-scrollbar-button {
                      display: none; /* Removes scrollbar arrows */
                    }
                  `}
                </style>
              </div>
            );
          },
          MultiValue: ({ children, components, data, removeProps, ...props }) => (
            <div style={customStyles.multiValue({})}>
              <div>{data.label}</div>
              <div
                className="remove-button"
                data-value={data.value}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemoveValue(data);
                }}
                style={customStyles.multiValueRemove({})}
              >
                <img src={close} />
              </div>
            </div>
          ),
        }}
      />
    </div>
  );
};

export default MultiSelectDropdown;